import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { vendorProfileService, VendorProfile, VendorLocale } from '../../services/vendorProfileService';
import { FormControl, MenuItem, Select } from '@mui/material';

interface CompanyInfoEditPopupProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedData: Partial<VendorProfile>) => void;
  vendorProfile: VendorProfile | null;
}

const CompanyInfoEditPopup: React.FC<CompanyInfoEditPopupProps> = ({
  open,
  onClose,
  onUpdate,
  vendorProfile
}) => {
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [shippingFeeInsideDoha, setShippingFeeInsideDoha] = useState<string>('');
  const [shippingFeeOutsideDoha, setShippingFeeOutsideDoha] = useState<string>('');
  const [currentLang, setCurrentLang] = useState<'en' | 'ar'>('en');
  const [locale, setLocale] = useState<VendorLocale>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with current vendor data when opened or vendorProfile changes
  useEffect(() => {
    if (vendorProfile && open) {
      // Initialize locale: if backend has no locale yet, seed English from top-level fields
      const initialLocale: VendorLocale =
        vendorProfile.locale ??
        ((vendorProfile.companyName || vendorProfile.companyDescription || vendorProfile.servicePolicy)
          ? {
              en: {
                ...(vendorProfile.companyName ? { companyName: vendorProfile.companyName } : {}),
                ...(vendorProfile.companyDescription
                  ? { companyDescription: vendorProfile.companyDescription }
                  : {}),
              },
            }
          : {});

      setLocale(initialLocale);
      setCurrentLang('en');

      const enEntry = initialLocale.en ?? {};
      setCompanyName(enEntry.companyName || vendorProfile.companyName || '');
      setCompanyDescription(
        enEntry.companyDescription || vendorProfile.companyDescription || ''
      );
      setCompanyWebsite(vendorProfile.companyWebsite || '');
      setShippingFeeInsideDoha(
        vendorProfile.shippingFeeInsideDoha !== undefined
          ? String(vendorProfile.shippingFeeInsideDoha)
          : ''
      );
      setShippingFeeOutsideDoha(
        vendorProfile.shippingFeeOutsideDoha !== undefined
          ? String(vendorProfile.shippingFeeOutsideDoha)
          : ''
      );
    }
  }, [vendorProfile, open]);

  const handleLanguageChange = useCallback(
    (newLang: 'en' | 'ar') => {
      // Snapshot current visible values into locale[currentLang]
      const trimmedName = companyName.trim();
      const trimmedDescription = companyDescription.trim();

      const nextLocale: VendorLocale = {
        ...locale,
        [currentLang]: {
          ...(locale[currentLang] || {}),
          ...(trimmedName ? { companyName: trimmedName } : {}),
          ...(trimmedDescription ? { companyDescription: trimmedDescription } : {}),
        },
      };

      setLocale(nextLocale);
      setCurrentLang(newLang);

      const targetEntry = nextLocale[newLang] || {};
      setCompanyName(
        targetEntry.companyName ||
          (newLang === 'en' ? vendorProfile?.companyName || '' : '') ||
          ''
      );
      setCompanyDescription(
        targetEntry.companyDescription ||
          (newLang === 'en' ? vendorProfile?.companyDescription || '' : '') ||
          ''
      );
    },
    [companyName, companyDescription, currentLang, locale, vendorProfile]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!vendorProfile) {
      toast.error('No vendor session found. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Build final locale with current language snapshot
      const finalLocale: VendorLocale = {
        ...locale,
        [currentLang]: {
          ...(locale[currentLang] || {}),
          ...(companyName.trim() ? { companyName: companyName.trim() } : {}),
          ...(companyDescription.trim() ? { companyDescription: companyDescription.trim() } : {}),
        },
      };

      const defaultCompanyName =
        finalLocale.en?.companyName ??
        (currentLang === 'en' ? companyName.trim() : vendorProfile.companyName || companyName.trim());

      const defaultCompanyDescription =
        finalLocale.en?.companyDescription ??
        (currentLang === 'en'
          ? companyDescription.trim()
          : vendorProfile.companyDescription || companyDescription.trim());

      const insideFeeNumber =
        shippingFeeInsideDoha.trim() === ''
          ? undefined
          : Number(shippingFeeInsideDoha.trim());
      const outsideFeeNumber =
        shippingFeeOutsideDoha.trim() === ''
          ? undefined
          : Number(shippingFeeOutsideDoha.trim());

      const editData = {
        companyName: defaultCompanyName,
        companyDescription: defaultCompanyDescription,
        companyWebsite: companyWebsite.trim(),
        ...(insideFeeNumber !== undefined && !Number.isNaN(insideFeeNumber)
          ? { shippingFeeInsideDoha: insideFeeNumber }
          : {}),
        ...(outsideFeeNumber !== undefined && !Number.isNaN(outsideFeeNumber)
          ? { shippingFeeOutsideDoha: outsideFeeNumber }
          : {}),
        locale: finalLocale,
      };

      const response = await vendorProfileService.editProfile(vendorProfile.vendorId, editData);

      if (response.success) {
        toast.success('Company information updated successfully!');
        onUpdate({
          ...editData,
        });
        handleClose();
      } else {
        toast.error(response.message || 'Failed to update company information');
      }
    } catch (error: any) {
      console.error('Error updating company info:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to update company information. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: '20px',
          color: '#181818'
        }}>
          Edit Company Information
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            py: 2
          }}
        >
          {/* Language selector */}
          <FormControl fullWidth size="small">
            <Select
              value={currentLang}
              onChange={(e) => handleLanguageChange(e.target.value as 'en' | 'ar')}
              displayEmpty
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="ar">Arabic</MenuItem>
            </Select>
          </FormControl>

          {/* Company Name Field */}
          <TextField
            fullWidth
            placeholder="Company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: companyName ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Company Description Field */}
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Company description"
            value={companyDescription}
            onChange={(e) => setCompanyDescription(e.target.value)}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: companyDescription ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Shipping Fee Inside Doha */}
          <TextField
            fullWidth
            type="number"
            placeholder="Shipping fee inside Doha (QAR)"
            value={shippingFeeInsideDoha}
            onChange={(e) => setShippingFeeInsideDoha(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: shippingFeeInsideDoha ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Shipping Fee Outside Doha */}
          <TextField
            fullWidth
            type="number"
            placeholder="Shipping fee outside Doha (QAR)"
            value={shippingFeeOutsideDoha}
            onChange={(e) => setShippingFeeOutsideDoha(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: shippingFeeOutsideDoha ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Company Website Field */}
          <TextField
            fullWidth
            placeholder="Company website"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: companyWebsite ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
          }}
        >
          {/* Update Button */}
          <Button
            onClick={handleSubmit}
            disabled={!companyName.trim() || !companyDescription.trim() || isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: '#67314f',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a2a42'
              },
              '&:disabled': {
                backgroundColor: '#93989c',
                color: 'white'
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Update Information'
            )}
          </Button>

          {/* Cancel Button */}
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: 'transparent',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: '#67314f',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyInfoEditPopup;
